import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import { 
  Users, 
  Activity, 
  TrendingUp, 
  Calendar,
  Shield,
  LogOut,
  RefreshCw,
  Search,
  ChevronLeft,
  Clock,
  Globe,
  Monitor,
  MapPin,
  Smartphone,
  Chrome,
  Eye,
  X,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';

export const AdminPanel = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    totalVisitors: 0,
    newVisitorsToday: 0,
    totalPageViews: 0,
    activeToday: 0
  });
  const [visitors, setVisitors] = useState([]);
  const [pageVisits, setPageVisits] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeTab, setActiveTab] = useState('visitors'); // visitors, pageVisits
  const [selectedVisitorId, setSelectedVisitorId] = useState(null); // Track selected visitor for filtering
  
  // Pagination state
  const [visitorsPage, setVisitorsPage] = useState(1);
  const [visitorsPerPage, setVisitorsPerPage] = useState(10);
  const [pageVisitsPage, setPageVisitsPage] = useState(1);
  const [pageVisitsPerPage, setPageVisitsPerPage] = useState(10);

  useEffect(() => {
    // Check if admin is authenticated
    const isAuthenticated = localStorage.getItem('admin_authenticated') === 'true';
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }

    fetchAdminData();
  }, [navigate]);

  const fetchAdminData = async () => {
    setLoading(true);
    try {
      // Fetch stats
      const statsRes = await fetch(`${API_URL}/admin/analytics/stats`);
      const statsData = await statsRes.json();
      if (statsData.success) {
        setStats(statsData.stats);
      }

      // Fetch visitors
      const visitorsRes = await fetch(`${API_URL}/admin/analytics/visitors`);
      const visitorsData = await visitorsRes.json();
      if (visitorsData.success) {
        setVisitors(visitorsData.visitors);
      }

      // Fetch page visits
      const visitsRes = await fetch(`${API_URL}/admin/analytics/page-visits`);
      const visitsData = await visitsRes.json();
      if (visitsData.success) {
        setPageVisits(visitsData.visits);
      }
    } catch (error) {
      console.error('Error fetching admin data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('admin_authenticated');
    localStorage.removeItem('admin_session_time');
    navigate('/login');
  };

  const filteredVisitors = visitors.filter(visitor => 
    (visitor.visitor_id && visitor.visitor_id.toLowerCase().includes(searchTerm.toLowerCase())) ||
    (visitor.ip_address && visitor.ip_address.includes(searchTerm)) ||
    (visitor.country && visitor.country.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  // Filter page visits by selected visitor if one is selected
  const filteredPageVisits = selectedVisitorId 
    ? pageVisits.filter(visit => visit.visitor_id === selectedVisitorId)
    : pageVisits;

  // Pagination calculations for visitors
  const visitorsTotalPages = Math.ceil(filteredVisitors.length / visitorsPerPage);
  const visitorsStartIndex = (visitorsPage - 1) * visitorsPerPage;
  const visitorsEndIndex = visitorsStartIndex + visitorsPerPage;
  const paginatedVisitors = filteredVisitors.slice(visitorsStartIndex, visitorsEndIndex);

  // Pagination calculations for page visits
  const pageVisitsTotalPages = Math.ceil(filteredPageVisits.length / pageVisitsPerPage);
  const pageVisitsStartIndex = (pageVisitsPage - 1) * pageVisitsPerPage;
  const pageVisitsEndIndex = pageVisitsStartIndex + pageVisitsPerPage;
  const paginatedPageVisits = filteredPageVisits.slice(pageVisitsStartIndex, pageVisitsEndIndex);

  // Reset pagination when search term changes
  useEffect(() => {
    setVisitorsPage(1);
  }, [searchTerm]);

  // Reset pagination when tab changes
  useEffect(() => {
    setVisitorsPage(1);
    setPageVisitsPage(1);
  }, [activeTab]);

  // Reset page visits pagination when visitor filter changes
  useEffect(() => {
    setPageVisitsPage(1);
  }, [selectedVisitorId]);

  // Get selected visitor details
  const selectedVisitor = selectedVisitorId 
    ? visitors.find(v => v.visitor_id === selectedVisitorId)
    : null;

  // Handle visitor row click
  const handleVisitorClick = (visitorId) => {
    setSelectedVisitorId(visitorId);
    setActiveTab('pageVisits');
  };

  // Clear visitor filter
  const clearVisitorFilter = () => {
    setSelectedVisitorId(null);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Pagination component
  const Pagination = ({ 
    currentPage, 
    totalPages, 
    onPageChange, 
    itemsPerPage, 
    onItemsPerPageChange,
    totalItems,
    startIndex,
    endIndex
  }) => {
    const getPageNumbers = () => {
      const pages = [];
      const maxVisible = 5;
      
      if (totalPages <= maxVisible) {
        for (let i = 1; i <= totalPages; i++) {
          pages.push(i);
        }
      } else {
        if (currentPage <= 3) {
          for (let i = 1; i <= 4; i++) {
            pages.push(i);
          }
          pages.push('...');
          pages.push(totalPages);
        } else if (currentPage >= totalPages - 2) {
          pages.push(1);
          pages.push('...');
          for (let i = totalPages - 3; i <= totalPages; i++) {
            pages.push(i);
          }
        } else {
          pages.push(1);
          pages.push('...');
          for (let i = currentPage - 1; i <= currentPage + 1; i++) {
            pages.push(i);
          }
          pages.push('...');
          pages.push(totalPages);
        }
      }
      
      return pages;
    };

    return (
      <div className="flex flex-col sm:flex-row items-center justify-between gap-4 mt-6 px-4 py-3 bg-gray-50 rounded-lg border border-gray-200">
        <div className="flex items-center gap-4">
          <span className="text-sm text-gray-600">
            Showing {startIndex + 1} to {Math.min(endIndex, totalItems)} of {totalItems} results
          </span>
          <select
            value={itemsPerPage}
            onChange={(e) => {
              onItemsPerPageChange(Number(e.target.value));
              onPageChange(1);
            }}
            className="px-3 py-1.5 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            <option value={10}>10 per page</option>
            <option value={25}>25 per page</option>
            <option value={50}>50 per page</option>
            <option value={100}>100 per page</option>
          </select>
        </div>
        
        {totalPages > 1 && (
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => onPageChange(1)}
              disabled={currentPage === 1}
              className="h-8 w-8 p-0"
            >
              <ChevronsLeft size={16} />
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => onPageChange(currentPage - 1)}
              disabled={currentPage === 1}
              className="h-8 w-8 p-0"
            >
              <ChevronLeft size={16} />
            </Button>
            
            <div className="flex items-center gap-1">
              {getPageNumbers().map((page, index) => (
                page === '...' ? (
                  <span key={`ellipsis-${index}`} className="px-2 text-gray-400">...</span>
                ) : (
                  <Button
                    key={page}
                    variant={currentPage === page ? "default" : "outline"}
                    size="sm"
                    onClick={() => onPageChange(page)}
                    className={`h-8 w-8 p-0 ${currentPage === page ? 'bg-blue-600 text-white' : ''}`}
                  >
                    {page}
                  </Button>
                )
              ))}
            </div>
            
            <Button
              variant="outline"
              size="sm"
              onClick={() => onPageChange(currentPage + 1)}
              disabled={currentPage === totalPages}
              className="h-8 w-8 p-0"
            >
              <ChevronRight size={16} />
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => onPageChange(totalPages)}
              disabled={currentPage === totalPages}
              className="h-8 w-8 p-0"
            >
              <ChevronsRight size={16} />
            </Button>
          </div>
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-blue-50/30 flex items-center justify-center">
        <div className="text-center">
          <RefreshCw size={48} className="mx-auto text-blue-500 mb-4 animate-spin" />
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Loading...</h1>
          <p className="text-gray-600">Fetching analytics data</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-blue-50/30">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate('/')}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <ChevronLeft size={24} />
              </button>
              <div>
                <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
                  <Shield size={28} className="text-blue-600" />
                  Admin Panel
                </h1>
                <p className="text-sm text-gray-600">TSI User Management</p>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <button
                onClick={fetchAdminData}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                title="Refresh data"
              >
                <RefreshCw size={20} />
              </button>
              <div className="flex items-center gap-3 border-l pl-4">
                <div className="w-10 h-10 rounded-full border-2 border-blue-600 bg-gradient-to-br from-blue-600 to-indigo-600 flex items-center justify-center">
                  <Shield size={24} className="text-white" />
                </div>
                <div className="hidden md:block">
                  <p className="font-semibold text-sm text-gray-900">Administrator</p>
                  <p className="text-xs text-gray-600">TSI Analytics</p>
                </div>
              </div>
              <button
                onClick={handleLogout}
                className="p-2 hover:bg-red-50 text-red-600 rounded-lg transition-colors"
                title="Logout"
              >
                <LogOut size={20} />
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-8">
        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <Card className="border-l-4 border-l-blue-500">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-gray-600 flex items-center gap-2">
                <Users size={18} className="text-blue-500" />
                Total Visitors
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-3xl font-bold text-gray-900">{stats.totalVisitors}</p>
            </CardContent>
          </Card>

          <Card className="border-l-4 border-l-green-500">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-gray-600 flex items-center gap-2">
                <TrendingUp size={18} className="text-green-500" />
                New Today
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-3xl font-bold text-gray-900">{stats.newVisitorsToday}</p>
            </CardContent>
          </Card>

          <Card className="border-l-4 border-l-purple-500">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-gray-600 flex items-center gap-2">
                <Eye size={18} className="text-purple-500" />
                Total Page Views
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-3xl font-bold text-gray-900">{stats.totalPageViews}</p>
            </CardContent>
          </Card>

          <Card className="border-l-4 border-l-orange-500">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-gray-600 flex items-center gap-2">
                <Calendar size={18} className="text-orange-500" />
                Active Today
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-3xl font-bold text-gray-900">{stats.activeToday}</p>
            </CardContent>
          </Card>
        </div>

        {/* Tabs */}
        <div className="mb-6 border-b border-gray-200">
          <div className="flex gap-8 overflow-x-auto">
            <button
              onClick={() => {
                setActiveTab('visitors');
                clearVisitorFilter();
              }}
              className={`pb-4 px-2 font-medium transition-colors border-b-2 whitespace-nowrap ${
                activeTab === 'visitors'
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-gray-600 hover:text-gray-900'
              }`}
            >
              <div className="flex items-center gap-2">
                <Users size={18} />
                All Visitors ({visitors.length})
              </div>
            </button>
            <button
              onClick={() => setActiveTab('pageVisits')}
              className={`pb-4 px-2 font-medium transition-colors border-b-2 whitespace-nowrap ${
                activeTab === 'pageVisits'
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-gray-600 hover:text-gray-900'
              }`}
            >
              <div className="flex items-center gap-2">
                <Eye size={18} />
                Page Visits ({selectedVisitorId ? filteredPageVisits.length : pageVisits.length})
              </div>
            </button>
          </div>
        </div>

        {/* Visitors Tab */}
        {activeTab === 'visitors' && (
          <div>
            {/* Search */}
            <div className="mb-6">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
                <input
                  type="text"
                  placeholder="Search visitors by ID, IP, or location..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </div>

            {/* Visitors Table */}
            <Card>
              <CardContent className="p-0">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50 border-b border-gray-200">
                      <tr>
                        <th className="text-left py-4 px-6 font-semibold text-sm text-gray-700">Visitor ID</th>
                        <th className="text-left py-4 px-6 font-semibold text-sm text-gray-700">First Visit</th>
                        <th className="text-left py-4 px-6 font-semibold text-sm text-gray-700">Last Visit</th>
                        <th className="text-left py-4 px-6 font-semibold text-sm text-gray-700">Location & IP</th>
                        <th className="text-center py-4 px-6 font-semibold text-sm text-gray-700">Page Views</th>
                      </tr>
                    </thead>
                    <tbody>
                      {paginatedVisitors.length === 0 ? (
                        <tr>
                          <td colSpan="5" className="py-12 text-center">
                            <div className="flex flex-col items-center justify-center">
                              <Users size={48} className="text-gray-300 mb-4" />
                              <p className="text-gray-500 text-lg font-medium">No visitors found</p>
                              {searchTerm && (
                                <p className="text-gray-400 text-sm mt-2">Try adjusting your search terms</p>
                              )}
                            </div>
                          </td>
                        </tr>
                      ) : (
                        paginatedVisitors.map((visitor) => (
                          <tr 
                            key={visitor.id} 
                            onClick={() => handleVisitorClick(visitor.visitor_id)}
                            className="border-b border-gray-100 hover:bg-blue-50 transition-colors cursor-pointer group"
                            title="Click to view this visitor's page visits"
                          >
                          <td className="py-4 px-6">
                            <div className="flex items-center gap-3">
                              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-indigo-500 flex items-center justify-center text-white font-bold group-hover:scale-110 transition-transform">
                                <Users size={20} />
                              </div>
                              <div>
                                <p className="font-medium text-gray-900 text-sm font-mono">{visitor.visitor_id.substring(0, 20)}...</p>
                              </div>
                            </div>
                          </td>
                          <td className="py-4 px-6 text-sm text-gray-600">
                            {formatDate(visitor.first_visit)}
                          </td>
                          <td className="py-4 px-6 text-sm text-gray-600">
                            {formatDate(visitor.last_visit)}
                          </td>
                          <td className="py-4 px-6 text-sm">
                            <div className="flex items-center gap-2 mb-1">
                              <MapPin size={14} className="text-gray-400" />
                              <span className="text-gray-900">
                                {visitor.city && visitor.country 
                                  ? `${visitor.city}, ${visitor.country}` 
                                  : visitor.country || 'Unknown'}
                              </span>
                            </div>
                            <div className="flex items-center gap-2">
                              <Globe size={14} className="text-gray-400" />
                              <span className="text-gray-600">{visitor.ip_address || 'N/A'}</span>
                            </div>
                          </td>
                          <td className="py-4 px-6 text-center">
                            <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800">
                              {visitor.page_views || 0}
                            </span>
                          </td>
                        </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
            
            {/* Pagination for Visitors */}
            <Pagination
              currentPage={visitorsPage}
              totalPages={visitorsTotalPages}
              onPageChange={setVisitorsPage}
              itemsPerPage={visitorsPerPage}
              onItemsPerPageChange={setVisitorsPerPage}
              totalItems={filteredVisitors.length}
              startIndex={visitorsStartIndex}
              endIndex={visitorsEndIndex}
            />
          </div>
        )}

        {/* Page Visits Tab */}
        {activeTab === 'pageVisits' && (
          <div>
            {/* Filter Banner */}
            {selectedVisitorId && selectedVisitor && (
              <div className="mb-6 bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-full bg-gradient-to-br from-blue-500 to-indigo-500 flex items-center justify-center text-white font-bold">
                      <Users size={24} />
                    </div>
                    <div>
                      <p className="text-sm text-gray-600 mb-1">Showing visits for:</p>
                      <p className="font-bold text-gray-900 font-mono text-sm">{selectedVisitor.visitor_id}</p>
                      <div className="flex items-center gap-4 mt-1">
                        {selectedVisitor.ip_address && (
                          <span className="text-xs text-gray-600 flex items-center gap-1">
                            <Globe size={12} />
                            {selectedVisitor.ip_address}
                          </span>
                        )}
                        {selectedVisitor.country && (
                          <span className="text-xs text-gray-600 flex items-center gap-1">
                            <MapPin size={12} />
                            {selectedVisitor.city ? `${selectedVisitor.city}, ${selectedVisitor.country}` : selectedVisitor.country}
                          </span>
                        )}
                        <span className="text-xs text-blue-600 font-semibold">
                          {filteredPageVisits.length} visit{filteredPageVisits.length !== 1 ? 's' : ''}
                        </span>
                      </div>
                    </div>
                  </div>
                  <Button
                    onClick={clearVisitorFilter}
                    variant="outline"
                    className="border-blue-300 text-blue-600 hover:bg-blue-100"
                  >
                    <X size={16} className="mr-2" />
                    Clear Filter
                  </Button>
                </div>
              </div>
            )}
            
            <Card>
              <CardContent className="p-0">
                <div className="overflow-x-auto">
                  <table className="w-full">
                  <thead className="bg-gray-50 border-b border-gray-200">
                    <tr>
                      <th className="text-left py-4 px-6 font-semibold text-sm text-gray-700">Visitor</th>
                      <th className="text-left py-4 px-6 font-semibold text-sm text-gray-700">Page Title</th>
                      <th className="text-left py-4 px-6 font-semibold text-sm text-gray-700">Page URL</th>
                      <th className="text-left py-4 px-6 font-semibold text-sm text-gray-700">Visit Time</th>
                      <th className="text-left py-4 px-6 font-semibold text-sm text-gray-700">Time Spent</th>
                      <th className="text-left py-4 px-6 font-semibold text-sm text-gray-700">Device & Browser</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredPageVisits.length === 0 ? (
                      <tr>
                        <td colSpan="6" className="py-12 text-center">
                          <div className="flex flex-col items-center justify-center">
                            <Eye size={48} className="text-gray-300 mb-4" />
                            <p className="text-gray-500 text-lg font-medium">No page visits found</p>
                            {selectedVisitorId && (
                              <p className="text-gray-400 text-sm mt-2">This visitor hasn't visited any pages yet</p>
                            )}
                          </div>
                        </td>
                      </tr>
                    ) : (
                      paginatedPageVisits.map((visit) => (
                        <tr key={visit.id} className="border-b border-gray-100 hover:bg-gray-50 transition-colors">
                        <td className="py-4 px-6">
                          <div className="flex items-center gap-3">
                            <div className="w-8 h-8 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center text-white font-bold text-xs">
                              <Users size={16} />
                            </div>
                            <div>
                              <p className="font-medium text-xs text-gray-900 font-mono">{visit.visitor_id ? visit.visitor_id.substring(0, 15) + '...' : 'Anonymous'}</p>
                              <p className="text-xs text-gray-600">{visit.ip_address || 'N/A'}</p>
                            </div>
                          </div>
                        </td>
                        <td className="py-4 px-6 text-sm text-gray-900">
                          {visit.page_title || 'Untitled'}
                        </td>
                        <td className="py-4 px-6 text-sm text-blue-600 hover:underline">
                          <a href={visit.page_url} target="_blank" rel="noopener noreferrer">
                            {visit.page_url.length > 40 ? visit.page_url.substring(0, 40) + '...' : visit.page_url}
                          </a>
                        </td>
                        <td className="py-4 px-6 text-sm text-gray-600">
                          <div className="flex items-center gap-2 mb-1">
                            <Clock size={14} className="text-gray-400" />
                            {formatDate(visit.visit_time)}
                          </div>
                          {visit.timezone && (
                            <div className="text-xs text-gray-500">{visit.timezone}</div>
                          )}
                        </td>
                        <td className="py-4 px-6 text-sm text-gray-600">
                          {visit.time_spent > 0 
                            ? `${Math.floor(visit.time_spent / 60)}m ${visit.time_spent % 60}s` 
                            : 'Just visited'}
                        </td>
                        <td className="py-4 px-6 text-sm">
                          <div className="flex items-center gap-2 mb-1">
                            <Chrome size={14} className="text-gray-400" />
                            <span className="text-gray-900">{visit.browser_name || 'Unknown'}</span>
                          </div>
                          <div className="flex items-center gap-2 mb-1">
                            <Monitor size={14} className="text-gray-400" />
                            <span className="text-gray-600">{visit.os_name || 'Unknown'}</span>
                          </div>
                          {visit.screen_width && visit.screen_height && (
                            <div className="text-xs text-gray-500">{visit.screen_width} × {visit.screen_height}</div>
                          )}
                        </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
          
          {/* Pagination for Page Visits */}
          {filteredPageVisits.length > 0 && (
            <Pagination
              currentPage={pageVisitsPage}
              totalPages={pageVisitsTotalPages}
              onPageChange={setPageVisitsPage}
              itemsPerPage={pageVisitsPerPage}
              onItemsPerPageChange={setPageVisitsPerPage}
              totalItems={filteredPageVisits.length}
              startIndex={pageVisitsStartIndex}
              endIndex={pageVisitsEndIndex}
            />
          )}
          </div>
        )}
      </div>
    </div>
  );
};


